import { ActionType, getType } from 'typesafe-actions';

import { User } from '../../types/api';
import { apiActions } from '../api/api.actions';
import { userActions } from './user.actions';
import { ColorStatus, GetFetchCombinedPogResponse, LabelStatus } from '../../types/responses';
import { defaultCustomizeValues } from '../assortment/assortment.reducer';
import { Checkboxes } from '../../types/models';
import { getUserPreferencesFromStorage, setUserPreferencesToStorage } from '../../helpers/sessionStorage.helper';
import { UserRole } from '../../helpers/utils';
import { AisleGeometry } from '../job/job.types';

export type AisleFilterState = {
  confidenceLevel: string[];
  status: string[]
};

export type SortKey =
  'confidenceHighLow' |
  'confidenceLowHigh' |
  'pogNumberHighLow' |
  'pogNumberLowHigh' |
  'categoryAZ' |
  'categoryZA' |
  'newOld' |
  'oldNew';

export const statusFiltersInitial = ['optimized', 'notOptimized'];

export const INITIAL_FILTERS = {
  confidenceLevel: ['GREEN', 'YELLOW', 'RED'],
  status: statusFiltersInitial
};

export type ConfidenceLevel = keyof ColorStatus;
export type Status = 'optimized' | 'notOptimized';
export type Aisle = {
  code: string;
  category: string,
  geometry: AisleGeometry;
  status: keyof ColorStatus;
  label_support: string;
  time_limit_in_seconds: number;
  label_status: LabelStatus;
};
export type FetchPogByStore = {
  id: string,
  store_num: string;
  aisle_code: string;
  aisle_category: string;
  business_category: string;
  sales_in_dollars: number;
  productivity_estimate: number;
  camps_dollar: number;
  lost_sales_total: number;
  sku_count: number;
  single_facing_sku_count: number;
  current_setup_low_wos_count: number,
  fill_trips: number;
  linear: number;
  height: number;
  depth: number;
  inv_date: string;
  delivery_date: string;
  audit: {
    created_by: string;
    created_date_time: string;
  },
  main_aisle_orientation: string;
  main_aisle_num: string;
  lost_sales_total_dollar: number;
  updated_at?: string;
  job_id?: string;
};

export type AisleExtended = Aisle & FetchPogByStore & { aisle: string, aisleForSorting: string, optimizedDate: string };

export type IUserPreferencesCompare = {
  totalSku?: number;
  totalFacing?: number;
  skuPrimaryFillIssues?: boolean;
};

export type userPreferences = {
  language: string,
  workflowTooltips: boolean,
  preAssortment: Checkboxes,
  compare: IUserPreferencesCompare,
  postAssortment: Checkboxes,
  pogSeed?: string[],
  otherSettings?: Record<string, unknown>,
  pogDashboard: string[],
  pogDashboardStatus: string[]
  labels: {}
};
export interface UserState {
  availableStores?: Array<string>;
  aisleFilters: AisleFilterState;
  availableAisles: Aisle[] | null;
  currentUser: User | null;
  datasetInfo: string;
  sortKey: SortKey;
  searchValue: string;
  fetchPogByStore: FetchPogByStore[] | null;
  fetchCombinedPog: GetFetchCombinedPogResponse[] | null;
  userPreferences: userPreferences;
  searchValueForVisualizer: string;
  isVisualizerSearchEnabled: boolean;
  isServerError: boolean;
  roles: UserRole[];
}

const defaultUserPreferences: userPreferences = {
  language: 'en' as string,
  workflowTooltips: true,
  preAssortment: defaultCustomizeValues,
  compare: {},
  postAssortment: defaultCustomizeValues,
  pogSeed: [],
  pogDashboard: [],
  pogDashboardStatus: [],
  labels: {},
};

export const INITIAL_STATE: UserState = {
  currentUser: null,
  availableAisles: null,
  sortKey: 'categoryAZ',
  aisleFilters: INITIAL_FILTERS,
  searchValue: '',
  datasetInfo: '',
  userPreferences: defaultUserPreferences,
  fetchPogByStore: null,
  fetchCombinedPog: null,
  searchValueForVisualizer: '',
  isVisualizerSearchEnabled: false,
  isServerError: false,
  roles: [],
};

export function user(
  state: UserState = INITIAL_STATE,
  action: ActionType<typeof apiActions | typeof userActions>
): UserState {
  switch (action.type) {
    case getType(apiActions.getAuth.success): {
      const { stores, roles } = action.payload;

      return {
        ...state,
        availableStores: stores.sort(),
        roles: roles,
      };
    }
    case getType(apiActions.getUserPreference.success): {
      saveUserPreferenceSession(action.payload);

      return {
        ...state,
        userPreferences: action.payload
      };
    }
    case getType(apiActions.saveUserPreference.success): {
      return {
        ...state,
      };
    }
    case getType(userActions.setLanguage): {
      return {
        ...state,
        userPreferences: { ...state.userPreferences, language: action.payload }
      };
    }
    case getType(userActions.setworkflowTooltips): {
      updateWorkflowTooltipsSession(action.payload);

      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          workflowTooltips: action.payload
        }
      };
    }
    case getType(userActions.setLabels): {
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          labels: action.payload
        }
      };
    }
    case getType(userActions.setAssortmentFields): {
      updateAssortmentFields(action.payload);

      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          ...action.payload
        }
      };
    }
    case getType(apiActions.getAvailableAisles.success): {
      return {
        ...state,
        availableAisles: action.payload,
      };
    }
    case getType(apiActions.getAvailableAisles.failure): {
      return {
        ...state,
        availableAisles: null
      };
    }
    case getType(apiActions.getDatasetInfo.success): {
      const { dataset_info: { inv_date: invDate = '' } = {}} = action.payload;

      return {
        ...state,
        datasetInfo: invDate
      };
    }
    case getType(apiActions.setUserInfo.success): {
      const { name, email } = action.payload;

      return {
        ...state,
        currentUser: {
          name,
          email,
        },
      };
    }
    case getType(apiActions.getFetchPogByStore.success): {
      return {
        ...state,
        fetchPogByStore: action.payload
      };
    }
    case getType(apiActions.getFetchCombinedPog.success): {
      return {
        ...state,
        fetchCombinedPog: action.payload
      };
    }
    case getType(userActions.setSortBy): {
      return {
        ...state,
        sortKey: action.payload
      };
    }
    case getType(userActions.setPogDashboardStatus): {
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          pogDashboardStatus: action.payload
        }
      };
    }

    case getType(userActions.setStatusFilter): {
      return {
        ...state,
        aisleFilters: {
          ...state.aisleFilters,
          status: action.payload
        }
      };
    }
    case getType(userActions.setsearchValueForVisualizer): {
      return {
        ...state,
        searchValueForVisualizer: action.payload
      };
    }
    case getType(userActions.setVisualizerSearchEnabled): {
      return {
        ...state,
        isVisualizerSearchEnabled: action.payload
      };
    }
    case getType(userActions.setInitialState): {
      return {
        ...state,
        availableAisles: null,
        aisleFilters: INITIAL_FILTERS,
        searchValue: '',
        searchValueForVisualizer: ''
      };
    }
    case getType(apiActions.setUserInfo.failure):
      return INITIAL_STATE;
    default:
      return state;
  }
}

const saveUserPreferenceSession = (preferences: userPreferences) => {
  if (Object.prototype.toString.call(preferences) == '[object Object]') {
    preferences.workflowTooltips = preferences.workflowTooltips?.toString() === 'true' ? true : false;
    setUserPreferencesToStorage(preferences);
  } else {
    setUserPreferencesToStorage(defaultUserPreferences);
  }
};

const updateWorkflowTooltipsSession = (show: boolean) => {
  try {
    const up = getUserPreferencesFromStorage();

    up.workflowTooltips = show;
    setUserPreferencesToStorage(up);
  } catch (e) {
    setUserPreferencesToStorage({ ...defaultUserPreferences, workflowTooltips: show });
  }
};

const updateAssortmentFields = (data: Partial<userPreferences>) => {
  try {
    const up = { ...getUserPreferencesFromStorage(), ...data };

    setUserPreferencesToStorage(up);
  } catch (e) {
    setUserPreferencesToStorage({ ...defaultUserPreferences, ...data });
  }
};
